<script lang="ts">
  // Workout · Intervals: the workout's target power against what was ridden, with
  // difficulty and focus.
  import type { ReportContext } from '../context'
  import { statsFor } from '../stats'
  import Badges from '../parts/Badges.svelte'
  import Brand from '../parts/Brand.svelte'
  import Diamonds from '../parts/Diamonds.svelte'
  import Fill from '../parts/Fill.svelte'
  import Frame from '../parts/Frame.svelte'
  import Icon from '../parts/Icon.svelte'
  import PowerChart from '../parts/PowerChart.svelte'
  import StatGrid from '../parts/StatGrid.svelte'
  import TitleBlock from '../parts/TitleBlock.svelte'

  export let ctx: ReportContext

  $: w = ctx.data.workout!
  $: a = ctx.data.activity
  $: stats = statsFor(ctx, ['stat.xss', 'stat.time', 'stat.xep', 'stat.power', 'stat.distance', 'stat.hr', 'stat.recovery'])
  $: program = (() => {
    const p = w.program
    if (!p) return null
    if (p.type === 'continuous') return 'Continuous Improvement'
    if (p.day && p.days_total && p.day > 0 && p.day <= p.days_total) return `${p.phase ? p.phase + ' phase · ' : ''}Day ${p.day} of ${p.days_total}`
    return null
  })()
  $: hasTargets = ctx.show('targets') && w.has_targets
  $: wide = ctx.size === 'landscape'
</script>

<Frame {ctx} base="radial-gradient(120% 70% at 0% 0%, #26343b 0%, #12181b 55%, #080a0b 100%)">
  <div class="xr-layout">
    <div class="xr-top">
      <span class="xr-eyebrow"><Icon name="pulse" size={30} stroke={2.6} color="var(--accent)" /> Workout</span>
      <Brand height={38} />
    </div>

    <div class="xr-head">
      <h2 class="xr-name">{w.name}</h2>
      {#if ctx.show('program') && program}
        <div class="xr-program">{program}</div>
      {/if}
    </div>

    <div class="xr-rating">
      {#if ctx.show('difficulty') && a.difficulty_stars}
        <div class="xr-diff">
          <Diamonds value={a.difficulty_stars} size={ctx.size === 'square' ? 38 : 46} color="var(--accent)" />
          <span class="xr-diff-label">{a.difficulty_label ?? ''}</span>
        </div>
      {/if}
      {#if ctx.show('focus') && a.focus}
        <div class="xr-focus">
          <Icon name="target" size={30} stroke={2.4} color="var(--accent)" />
          <span><strong>{a.focus}</strong>{a.focus_duration ? ` · ${ctx.fmt.focusDuration(a.focus_duration)} focus` : ''}</span>
        </div>
      {/if}
    </div>

    <div class="xr-chart">
      {#if ctx.show('chart') && ctx.data.series?.power}
        <div class="xr-legend">
          {#if hasTargets}<span><i class="xr-dash" /> Target</span>{/if}
          <span><i class="xr-solid" /> Power</span>
        </div>
        <Fill minHeight={140} let:width let:height>
          <PowerChart series={ctx.data.series} colored={ctx.options.colorPowerChart} showHr={ctx.options.chartHeartRate} {width} {height} showTarget={hasTargets} showMpa={false} breakthrough={ctx.data.breakthrough?.level ?? null} fillOpacity={0.55} />
        </Fill>
      {/if}
    </div>

    <div class="xr-stats"><StatGrid {stats} columns={wide ? 2 : 4} size="sm" /></div>
    <div class="xr-meta"><TitleBlock {ctx} lines={1} /></div>
    <div class="xr-badges"><Badges {ctx} own="workout" max={ctx.size === 'square' ? 2 : Infinity} /></div>
  </div>
</Frame>

<style>
  .xr-layout {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto auto minmax(0, 1fr) auto auto auto;
    grid-template-areas: 'top' 'head' 'rating' 'chart' 'stats' 'meta' 'badges';
    row-gap: 28px;
  }
  :global(.xr-square) .xr-layout {
    row-gap: 22px;
  }
  /* Landscape: the workout on the left, the ride against its targets across the right. */
  :global(.xr-landscape) .xr-layout {
    grid-template-columns: 560px minmax(0, 1fr);
    grid-template-rows: auto auto auto minmax(0, 1fr) auto auto;
    grid-template-areas:
      'top top'
      'head chart'
      'rating chart'
      'stats chart'
      'meta chart'
      'badges badges';
    column-gap: 80px;
    row-gap: 28px;
  }
  .xr-top { grid-area: top; display: flex; align-items: center; justify-content: space-between; }
  .xr-head { grid-area: head; min-width: 0; }
  .xr-rating { grid-area: rating; }
  .xr-chart { grid-area: chart; display: flex; flex-direction: column; min-height: 0; }
  .xr-stats { grid-area: stats; }
  :global(.xr-landscape) .xr-stats { align-self: end; }
  .xr-meta { grid-area: meta; min-width: 0; }
  .xr-badges { grid-area: badges; }
  .xr-badges:empty { display: none; }
  :global(.xr-square) .xr-name { font-size: 88px; }
  :global(.xr-landscape) .xr-name { font-size: 96px; -webkit-line-clamp: 3; }
  .xr-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    font-size: 26px;
    font-weight: 800;
    letter-spacing: 0.28em;
    text-transform: uppercase;
  }
  .xr-name {
    font-family: var(--display);
    font-weight: 800;
    font-size: 108px;
    line-height: 0.92;
    text-transform: uppercase;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    overflow-wrap: anywhere;
  }
  .xr-program {
    margin-top: 16px;
    font-size: 30px;
    font-weight: 600;
    color: var(--accent);
  }
  .xr-rating {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 22px 48px;
  }
  .xr-diff {
    display: flex;
    align-items: center;
    gap: 18px;
  }
  .xr-diff-label {
    font-family: var(--display);
    font-size: 48px;
    font-weight: 700;
    text-transform: uppercase;
  }
  .xr-focus {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 30px;
    color: var(--muted);
  }
  .xr-focus strong {
    color: var(--accent);
    font-weight: 700;
  }
  .xr-legend {
    display: flex;
    gap: 28px;
    margin-bottom: 14px;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--muted);
  }
  .xr-legend span {
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }
  .xr-legend i {
    display: inline-block;
    width: 36px;
    height: 0;
  }
  .xr-dash {
    border-top: 4px dashed #fff;
  }
  .xr-solid {
    border-top: 6px solid var(--accent);
  }
</style>
